import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Container, Stack } from "@mui/system";
import SearchMdIcon from "@untitled-ui/icons-react/build/esm/SearchMd";
import {
  Checkbox,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  OutlinedInput,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { v4 as uuid4 } from "uuid";

import { Scrollbar } from "src/components/scrollbar";
import { reorder } from "src/utils/function";
import CustomModal from "./customize/custom-modal";
import TextField from "@mui/material/TextField";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-hot-toast";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { customerFieldsApi } from "../api/customer-fields";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCaretDown,
  faCircleCaretUp,
  faCircleSort,
  faEdit,
  faSliders,
  faTrash,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { DeleteModal } from "./customize/delete-modal";
import { CustomerFieldUpdate } from "../sections/dashboard/customer/customer-field-update";
import { statusApi } from "../api/lead-management/status";
import { useDebounce } from "src/hooks/use-debounce";
import { useAuth } from "src/hooks/use-auth";
import { faThumbTack } from "@fortawesome/pro-solid-svg-icons";
import { faThumbTack as faThumbTackDisabled } from "@fortawesome/pro-regular-svg-icons";

const validationSchema = yup.object({
  name: yup.string().required("Name is a required field"),
});

const IconVisibility = ({
  openIconSetting,
  setOpenIconSetting,
  fieldToEdit,
  updateIconSetting,
}) => {
  const [iconSetting, setIconSetting] = useState(fieldToEdit);

  const handleSwitch = (target, value) => {
    setIconSetting((prev) => ({ ...prev, [target]: value }));
  };

  const handleUpdate = () => {
    updateIconSetting(iconSetting);
  };

  useEffect(() => {
    setIconSetting(fieldToEdit?.subEnabled);
  }, [fieldToEdit]);

  return (
    <CustomModal
      onClose={() => {
        setOpenIconSetting(false);
      }}
      open={openIconSetting}
    >
      <Stack direction="row" justifyContent="center" pb={3}>
        <Typography sx={{ fontSize: 20, fontWeight: "600" }}>
          Icon visibility
        </Typography>
      </Stack>
      <Table>
        <TableHead>
          <TableCell sx={{ width: 220 }}>Icon</TableCell>
          <TableCell>Enabled</TableCell>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Info</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.info ?? false}
                onChange={(event) =>
                  handleSwitch("info", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Reminder</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.reminder ?? false}
                onChange={(event) =>
                  handleSwitch("reminder", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Phone</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.phone ?? false}
                onChange={(event) =>
                  handleSwitch("phone", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Agent Label</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.label ?? false}
                onChange={(event) =>
                  handleSwitch("label", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Custom Field</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.field ?? false}
                onChange={(event) =>
                  handleSwitch("field", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Open Chat</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.chat ?? false}
                onChange={(event) =>
                  handleSwitch("chat", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Note</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.note ?? false}
                onChange={(event) =>
                  handleSwitch("note", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Comment</TableCell>
            <TableCell>
              <Checkbox
                checked={iconSetting?.comment ?? false}
                onChange={(event) =>
                  handleSwitch("comment", event?.target?.checked)
                }
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Stack direction="row" pt={2} justifyContent="end">
        <Button
          variant="contained"
          size="medium"
          onClick={() => handleUpdate()}
        >
          Update
        </Button>
      </Stack>
    </CustomModal>
  );
};

export const TableModal = ({
  open,
  onClose,
  tableColumn = [],
  defaultColumn = [],
  updateRule,
  variant,
  sorting = null,
  pinnedFields = [],
  onPinnedFieldsSet = () => { },
  isPlatform = false,
  syncCustomerFieldsDerived = () => { }
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) });
  const { user } = useAuth();

  const [columnList, setColumList] = useState([]);
  const [createFieldModalOpen, setCreateFieldModalOpen] = useState(false);
  const [fieldType, setFieldType] = useState(1);
  const [fieldOptions, setFieldOptions] = useState([
    {
      id: uuid4(),
      option: "",
    },
  ]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedCustomField, setSelectedCustomField] = useState(null);
  const [selectedSetting, setSelectedSetting] = useState("");
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [search, setSearch] = useState("");
  const query = useDebounce(search, 300);
  const [customFields, setCustomFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortingState, setSortingState] = useState({});

  const [openIconSetting, setOpenIconSetting] = useState(false);

  useEffect(() => {
    setColumList(tableColumn);
  }, [tableColumn]);

  useEffect(() => {
    if (sorting) setSortingState(sorting);
  }, [sorting]);

  const getItemStyle = ({ theme, isDragging }) => {
    const table = document.querySelector(".signature-table");
    const tableHeader = table?.querySelector("thead");
    const tableBody = table?.querySelector("tbody");
    const theads = tableHeader?.querySelectorAll("th");
    const tr = tableBody?.querySelector("tr");
    const trStyle = {};
    theads?.forEach((th, index) => {
      trStyle[`& td:nth-of-type(${index + 1})`] = {
        width: th.clientWidth ? `${th.clientWidth}px` : "auto",
      };
    });
    if (isDragging)
      table?.style.setProperty("margin-bottom", `${tr?.clientHeight}px`);
    else table?.style.removeProperty("margin-bottom");
    return {
      ...(isDragging
        ? {
          borderRadius: `${theme.shape.borderRadius}px`,
          background: theme.palette.background.paper,
          ...trStyle,
        }
        : {}),
    };
  };
  const getCustomFields = async () => {
    try {
      if (variant === "customer") {
        const { client_fields } = await customerFieldsApi.getCustomerFields();
        setCustomFields(client_fields);
      } else {
        const { lead_fields } = await statusApi.getLeadCustomFields();
        setCustomFields(lead_fields);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    getCustomFields();
    setValue("sync_lead", false);
  }, [open]);

  const handleSwitch = (label, value) => {
    const newTableData = [
      ...columnList.map((item) => {
        if (item.label === label) {
          return {
            ...item,
            enabled: value,
          };
        } else {
          return item;
        }
      }),
    ];
    setColumList(newTableData);
  };

  const handleUpdateSetting = (label, value) => {
    const newTableData = [
      ...columnList.map((item) => {
        if (item.label === label) {
          return {
            ...item,
            subEnabled: value,
          };
        } else {
          return item;
        }
      }),
    ];
    setColumList(newTableData);
    setOpenIconSetting(false);
  };

  const handleChangeFieldType = (e) => setFieldType(e?.target?.value);

  const setDefault = () => {
    setColumList(defaultColumn);
    setSortingState({});
  };

  const handleResetSorting = () => {
    setSortingState({});

    const rule = columnList?.map((item, index) =>
      item?.id === "call_chat"
        ? {
          id: item?.id,
          enabled: item?.enabled,
          order: index,
          subEnabled: item?.subEnabled,
        }
        : {
          id: item?.id,
          enabled: item?.enabled,
          order: index,
        }
    );
    updateRule(rule, {}, pinnedFields, '');
    onClose();
  };

  const handleUpdateRule = (type = '') => {
    const rule = columnList?.map((item, index) =>
      item?.id === "call_chat"
        ? {
          id: item?.id,
          enabled: item?.enabled,
          order: index,
          subEnabled: item?.subEnabled,
        }
        : {
          id: item?.id,
          enabled: item?.enabled,
          order: index,
        }
    );
    updateRule(rule, sortingState, pinnedFields, type);
    onClose();
  };

  const containsOnlyLettersAndSpaces = (inputString) => {
    // Define a regular expression pattern that allows only letters and spaces
    var pattern = /^[a-zA-Z0-9\s]+$/;

    // Test the input string against the pattern
    return pattern.test(inputString);
  };

  const onCustomerFieldSubmit = async (data) => {
    try {
      setIsLoading(true);
      const fieldTypeValue = {
        1: "text",
        2: "number",
        3: "multi_choice",
        4: "boolean",
      };

      if (!containsOnlyLettersAndSpaces(data?.name)) {
        toast.error("Field name should not contain symbols!");
        setIsLoading(false);
        return;
      }

      const request = {
        value: data.name.replace(/\s+/g, "_"),
        friendly_name: data.name,
        field_type:
          fieldType === 3
            ? data?.multi_choice_radio
              ? "multi_choice"
              : "multi_choice_radio"
            : fieldTypeValue[fieldType],
      };
      request.sync_lead = data.sync_lead;
      if (fieldType === 3) {
        request["setting"] = JSON.stringify(fieldOptions);
      }

      if (variant === "customer") {
        const { client_field } = await customerFieldsApi.createCustomerField(
          request
        );
        setColumList((prev) => [
          ...prev,
          ...[
            {
              ...client_field,
              label: client_field?.friendly_name,
              id: client_field?.value,
              enabled: true,
              custom_id: client_field?.id,
              custom: true,
              render: (row) => row?.client_fields[client_field?.id],
            },
          ],
        ]);
        const newFilter = {
          ...client_field,
          label: client_field?.friendly_name,
          enabled: true,
          custom_id: client_field?.id,
          custom: true,
        }

        toast.success("Customer field successfully created!");
        setCreateFieldModalOpen(false);
        reset();
        setFieldType(1);
        setFieldOptions([
          {
            id: uuid4(),
            option: "",
          },
        ]);
        syncCustomerFieldsDerived("create",
          {
            ...newFilter,
          }
        )
        setTimeout(() => {
          getCustomFields();
        }, 1000);
      }

      if (variant === "lead") {
        const { lead_field } = await statusApi.createLeadCustomField(request);
        setColumList((prev) => [
          ...prev,
          ...[
            {
              ...lead_field,
              label: lead_field?.friendly_name,
              id: lead_field?.value,
              enabled: true,
              custom_id: lead_field?.id,
              custom: true,
              render: (row) => row?.lead_fields[lead_field?.id],
            },
          ],
        ]);
        toast.success("Customer field successfully created!");
        reset();
        setCreateFieldModalOpen(false);
        setTimeout(() => {
          getCustomFields();
        }, 1000);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toast.error(error?.response?.data?.message);
    }
  };

  const handleAddOption = () =>
    setFieldOptions((prev) => [...prev, ...[{ option: "", id: uuid4() }]]);

  const handleChangeField = (e, id, type = "option") => {
    e.preventDefault();

    if (type === "option") {
      setFieldOptions(
        fieldOptions.map((option) => {
          if (id === option?.id) {
            return {
              ...option,
              option: e?.target?.value,
            };
          } else {
            return option;
          }
        })
      );
    } else {
      setFieldOptions(
        fieldOptions.map((option) => {
          if (id === option?.id) {
            return {
              ...option,
              color: e?.target?.value,
            };
          } else {
            return option;
          }
        })
      );
    }
  };

  const handleRemoveField = async () => {
    try {
      if (variant === "customer") {
        await customerFieldsApi.deleteCustomerField(selectedCustomField);
      }
      if (variant === "lead") {
        await statusApi.deleteLeadCustomField(selectedCustomField);
      }
      setColumList((prev) =>
        prev?.filter((item) => item?.custom_id !== selectedCustomField)
      );
      setSelectedCustomField(null);
      setOpenDelete(false);
      toast.success("Custom field successfully deleted!");
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    reset();
    setFieldOptions([
      {
        id: uuid4(),
        option: "",
      },
    ]);
    setFieldType(1);
  }, [createFieldModalOpen]);

  const renderSortingIcon = (variant, label, custom = false) => {
    if (variant === true) {
      return (
        <Tooltip title="Ascending">
          <IconButton
            onClick={() => handleSortingChange(label, false, custom)}
            sx={{ opacity: ".5" }}
          >
            <FontAwesomeIcon icon={faCircleCaretUp} />
          </IconButton>
        </Tooltip>
      );
    } else if (variant === false) {
      return (
        <Tooltip title="Descending">
          <IconButton
            onClick={() => handleSortingChange(label, undefined, custom)}
            sx={{ opacity: ".5" }}
          >
            <FontAwesomeIcon icon={faCircleCaretDown} />
          </IconButton>
        </Tooltip>
      );
    } else if (variant === undefined) {
      return (
        <Tooltip title="Disabled">
          <IconButton
            onClick={() => handleSortingChange(label, true, custom)}
            sx={{ opacity: ".5" }}
          >
            <FontAwesomeIcon icon={faCircleSort} />
          </IconButton>
        </Tooltip>
      );
    }
  };

  const handleSortingChange = (column, variant, custom) => {
    setSortingState((prev) => ({
      ...prev,
      [custom ? 'c_' + column : column]: variant,
    }));
  };

  const handleItemPin = (id, pinned) => {
    if (pinned) {
      onPinnedFieldsSet((prev) => prev?.filter((item) => item !== id));
    } else {
      onPinnedFieldsSet([...pinnedFields, id]);
    }
  };

  const filteredColumnList = useMemo(() => {
    if (query) {
      return columnList
        ?.filter((item) =>
          item?.label?.toLowerCase()?.includes(query?.toLowerCase())
        )
        ?.map((item) => {
          const accessEditkey = `acc_custom_e_${item?.id}`;
          const editAccess = user?.acc && (user?.acc[accessEditkey] === undefined || user?.acc[accessEditkey]);
          return {
            ...item,
            sorting: sortingState[item?.custom ? "c_" + item?.label : item?.label],
            pinned: pinnedFields.includes(item?.custom_id),
            editAccess,
          }
        });
    } else {
      return columnList
        ?.map((item) => {
          const accessEditkey = `acc_custom_e_${item?.id}`;
          const editAccess = user?.acc && (user?.acc[accessEditkey] === undefined || user?.acc[accessEditkey]);
          return {
            ...item,
            sorting: sortingState[item?.custom ? "c_" + item?.label : item?.label],
            pinned: pinnedFields.includes(item?.custom_id),
            editAccess,
          }
        });
    }
  }, [query, columnList, user, sortingState, pinnedFields]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newTableData = reorder(
      filteredColumnList,
      result.source.index,
      result.destination.index
    );
    setColumList(newTableData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <Stack mt={2} py={3} direction="row" justifyContent="center">
        <Typography variant="h5">Table Column Setting</Typography>
      </Stack>
      <Scrollbar sx={{ maxHeight: "500px" }}>
        <Container maxWidth="sm">
          <Stack direction="column" spacing={3} pb={3}>
            <TextField
              fullWidth
              sx={{ pt: "5px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon>
                      <SearchMdIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
              }}
              label="Search"
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder="Search columns..."
              value={search}
            />
            <Table className="signature-table">
              <TableHead>
                <TableCell>Name</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Actions</TableCell>
                {variant === "customer" && !isPlatform && (
                  <TableCell>Sorting</TableCell>
                )}
                {variant === "customer" && <TableCell>Pin</TableCell>}
              </TableHead>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {({ innerRef }) => (
                    <Box component={TableBody} ref={innerRef}>
                      {filteredColumnList?.map((row, index) => (
                        <Draggable
                          key={`signature-row-${index}`}
                          draggableId={`signature-row-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Box
                              component={TableRow}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={(theme) => getItemStyle({
                                theme,
                                isDragging: snapshot.isDragging,
                              })}
                            >
                              <TableCell sx={{ width: 350 }}>
                                <Stack direction="row" gap={1}>
                                  <DragIndicatorIcon />
                                  <Typography>{row.label}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={row.enabled}
                                  onChange={(event) => handleSwitch(
                                    row.label,
                                    event.target.checked
                                  )} />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={2}>
                                  {row?.subSetting ? (
                                    <IconButton
                                      onClick={() => {
                                        setFieldToEdit(row);
                                        setOpenIconSetting(true);
                                      }}
                                    >
                                      <Tooltip title="Edit Call & Chat Settings">
                                        <FontAwesomeIcon
                                          icon={faSliders}
                                          size="sm" />
                                      </Tooltip>
                                    </IconButton>
                                  ) : null}
                                  {(row?.custom && variant === "lead" && !user?.affiliate) || (row?.custom && user?.acc?.acc_e_client_data && row?.editAccess) ? (
                                    <>
                                      <IconButton
                                        onClick={() => {
                                          setSelectedCustomField(
                                            row?.custom_id
                                          );
                                          setSelectedSetting(row?.setting);
                                          setFieldToEdit(row);
                                          setOpenEdit(true);
                                        }}
                                      >
                                        <Tooltip title="Edit Custom Field">
                                          <FontAwesomeIcon
                                            icon={faEdit}
                                            size="sm" />
                                        </Tooltip>
                                      </IconButton>
                                      <IconButton
                                        onClick={() => {
                                          setSelectedCustomField(
                                            row?.custom_id
                                          );
                                          setOpenDelete(true);
                                        }}
                                      >
                                        <Tooltip title="Delete Label">
                                          <FontAwesomeIcon
                                            icon={faTrash}
                                            size="sm" />
                                        </Tooltip>
                                      </IconButton>
                                    </>
                                  ) : null}
                                </Stack>
                              </TableCell>
                              {variant === "customer" && !isPlatform && (
                                <TableCell>
                                  {row?.hasSort === false
                                    ? null :
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                    >
                                      {renderSortingIcon(
                                        row?.sorting,
                                        row?.label,
                                        row?.custom
                                      )}
                                    </Stack>}
                                </TableCell>
                              )}
                              {variant === "customer" && (
                                <TableCell>
                                  {row?.custom ? (
                                    <Tooltip title="Pin">
                                      <IconButton
                                        onClick={() => handleItemPin(
                                          row?.custom_id,
                                          row?.pinned
                                        )}
                                      >
                                        <FontAwesomeIcon
                                          icon={row?.pinned
                                            ? faThumbTack
                                            : faThumbTackDisabled} />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </TableCell>
                              )}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </Stack>
        </Container>
      </Scrollbar>
      {isPlatform ? (
        <Stack direction="row" justifyContent="end" px={5} py={3} gap={2}>
          <Button variant="outlined" onClick={() => handleUpdateRule('default')}>
            Save for new users
          </Button>
          <Button onClick={() => handleUpdateRule('existing')} variant="contained">
            Apply for existing users
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="end" px={5} py={3} gap={2}>
          {variant === 'customer' && (
            <Button variant="outlined" onClick={handleResetSorting}>
              Reset Sorting
            </Button>
          )}
          <Button variant="outlined" onClick={setDefault}>
            Default
          </Button>
          {variant && !user?.affiliate && (
            <Button
              variant="outlined"
              onClick={() => setCreateFieldModalOpen(true)}
            >
              Create field
            </Button>
          )}
          <Button onClick={handleUpdateRule} variant="contained">
            Update
          </Button>
        </Stack>
      )}

      <IconVisibility
        openIconSetting={openIconSetting}
        setOpenIconSetting={setOpenIconSetting}
        fieldToEdit={fieldToEdit}
        updateIconSetting={(val) => {
          handleUpdateSetting(fieldToEdit.label, val);
        }}
      />

      <CustomModal
        onClose={() => {
          setCreateFieldModalOpen(false);
          reset();
        }}
        open={createFieldModalOpen}
      >
        <form onSubmit={handleSubmit(onCustomerFieldSubmit)}>
          <Stack spacing={2}>
            <Typography
              id="modal-modal-title"
              align="center"
              sx={{ fontSize: 22, fontWeight: "bold", mt: 1 }}
            >
              Create Custom Field
            </Typography>
            <Stack direction="column">
              <Stack sx={{ py: 2 }} spacing={1} justifyContent="center">
                <Typography>Name</Typography>
                <TextField
                  fullWidth
                  autoFocus
                  error={!!errors?.name?.message}
                  helperText={errors?.name?.message}
                  label="Name"
                  name="name"
                  type="text"
                  {...register("name")}
                />
              </Stack>
              <Controller
                name="sync_lead"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    sx={{ userSelect: "none" }}
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(event) => onChange(event?.target?.checked)}
                      />
                    }
                    label="Sync with upcoming lead"
                  />
                )}
              />
              {fieldType === 3 ? (
                <Controller
                  name="multi_choice_radio"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      sx={{ userSelect: "none" }}
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => onChange(event?.target?.checked)}
                        />
                      }
                      label="Multi-choice dropdown"
                    />
                  )}
                />
              ) : null}
            </Stack>
            <Stack sx={{ py: 2 }} spacing={1} justifyContent="center">
              <Typography>Type</Typography>
              <Select
                value={fieldType}
                onChange={handleChangeFieldType}
                sx={{ width: "100%" }}
              >
                <MenuItem value={1}>Text</MenuItem>
                <MenuItem value={2}>Number</MenuItem>
                <MenuItem value={3}>Dropdown</MenuItem>
                <MenuItem value={4}>Switch</MenuItem>
              </Select>
            </Stack>
            {fieldType === 3 && (
              <Stack spacing={3}>
                <Typography variant={"h6"}>Create options:</Typography>
                <Scrollbar sx={{ maxHeight: "300px" }}>
                  <Stack spacing={1} pb={2}>
                    {fieldOptions?.map((option, index) => (
                      <Stack direction="row" spacing={1} key={option?.id}>
                        <OutlinedInput
                          value={option.option}
                          placeholder={`Option ${index + 1}`}
                          onChange={(e) => handleChangeField(e, option?.id)}
                          sx={{ width: "100%" }}
                        />
                        <Stack direction="row" alignItems="center" gap={1}>
                          <label htmlFor={option.id}>
                            <Chip
                              label={option?.color ?? "Default"}
                              color="primary"
                              sx={{ backgroundColor: option?.color ?? "" }}
                            />
                          </label>
                          <input
                            style={{
                              visibility: "hidden",
                              width: "0px",
                              height: "0px",
                            }}
                            id={option.id}
                            type="color"
                            onChange={(e) =>
                              handleChangeField(e, option?.id, "color")
                            }
                          />
                        </Stack>
                        {index !== 0 && (
                          <IconButton
                            onClick={() =>
                              setFieldOptions(
                                fieldOptions?.filter(
                                  (opt) => opt?.id !== option?.id
                                )
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Scrollbar>
                <Button variant={"contained"} onClick={handleAddOption}>
                  + Add
                </Button>
              </Stack>
            )}
            <Stack sx={{ gap: 2 }} direction="row" justifyContent="center">
              <Button variant="contained" type="submit" disabled={isLoading}>
                Create
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCreateFieldModalOpen(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </CustomModal>

      <DeleteModal
        isOpen={openDelete}
        setIsOpen={setOpenDelete}
        title="Are you sure you want to delete this custom field?"
        onDelete={handleRemoveField}
      />

      {!!fieldToEdit && (
        <CustomerFieldUpdate
          fields={customFields}
          field={fieldToEdit}
          open={openEdit}
          fieldId={selectedCustomField}
          fieldSetting={selectedSetting}
          variant={variant}
          onClose={() => {
            setOpenEdit(false);
            setFieldToEdit(null);
          }}
          onSetList={setColumList}
          onUpdateRule={handleUpdateRule}
          onGetFields={getCustomFields}
          getCustomFieldsDerived={syncCustomerFieldsDerived}
        />
      )}
    </Dialog>
  );
};
